import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/react-hooks';
import type { ActionFn, Action, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import DataGrid, { defaultActions } from '#/common/components/DataGrid';
import { getModelList } from '#/graphql/queries';
import { Model } from '#/graphql/types';
import useModelDelete from '../../hooks/useDeleteModelMutation';
import columns from './columns';
import { actions } from './actions';
import { UserRole } from '#/common/types/userTypes';
import { inUseStatuses } from '../../utils/constants';

const getModelListQuery = gql(getModelList);

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-model'],
  [UserRole.trane_admin]: ['delete-model'],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: [
    'new',
    ...defaultActions.map((a) => (typeof a === 'string' ? a : a.key)),
  ],
  [UserRole.trane_admin]: [
    'new',
    ...defaultActions.map((a) => (typeof a === 'string' ? a : a.key)),
  ],
  [UserRole.technician]: [...defaultActions.map((a) => (typeof a === 'string' ? a : a.key))],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Model>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const ModelDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { user } = useSession();
  const { deleteModel } = useModelDelete();

  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these models?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteModel(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteModel],
  );

  const userSelectedActions: ActionFn<Model>[] = useMemo(() => {
    const res: ActionFn<Model>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-model')) {
      res.push({
        key: 'delete-model',
        icon: 'trash',
        text: 'Delete',
        disabled: (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (model) =>
              model.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(model.thirdPartyId && model.thirdPartySource),
          );
          let count = filteredItemsForDelete?.length;
          return count < 1;
        },
        onClick: async (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (model) =>
              model.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(model.thirdPartyId && model.thirdPartySource),
          );

          if (filteredItemsForDelete.length < e.selectedItems.length) {
            const sure = await confirm(
              'error',
              `Some selected models are not in 'Newly Created' status and can not be deleted.`,
            );
            if (!sure) {
              return;
            }
          }
          await deleteItem(
            filteredItemsForDelete.map((model) => model.id!),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }
    return res;
  }, [role, deleteItem]);

  const gridActions = useMemo<Action<Model>[]>(() => {
    return actions
      .filter((a) => role && gridActionsPerRole[role]?.includes(typeof a === 'string' ? a : a.key))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  if (!role) return null;

  return (
    <DataGrid
      id="models"
      pluralText="Models"
      singularText="Model"
      columns={columns}
      recordUrl="/assets/models/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getModelListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(ModelDataGrid);
